import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Alert, Box, Button, Chip, CircularProgress, FormControl, FormHelperText, InputLabel, MenuItem,
    Paper, Select, SelectChangeEvent, Table, TableBody, TableCell, TableContainer, TableHead,
    TablePagination, TableRow, TextField, Typography,
} from '@mui/material';
import { PosService } from '../../services/pos.service';
import { getStoresByInstitutionId } from '../../services/store.service';
import { useAuth } from '../../context/AuthContext';
import { POS_STORE_STAFF_ROLES } from '../../constants/posRoles';
import { useToast } from '../../context/ToastContext';
import { PosSaleSummary, PosSaleStatus, PosPostingStatus, PosTerminal, PosStoreAssignment } from '../../interfaces/pos.interface';
import { Store } from '../../interfaces/store.interface';
import { formatMoney } from '../../utils/currency';
import { formatDateTime, toDayEndIso, toDayStartIso } from '../../utils/dateUtils';

// Same cap/comment as PosTerminalList.tsx's STORE_PAGE_SIZE.
const STORE_PAGE_SIZE = 100;

const SALE_STATUS_COLORS: Record<PosSaleStatus, 'success' | 'default'> = {
    COMPLETED: 'success',
    VOIDED: 'default',
};

// REVERSAL_FAILED is deliberately NOT rendered as an error here (spec: a successful void that
// left the ledger reversal unresolved is not a failed void) -- 'warning' matches PENDING/FAILED's
// "needs attention" register without reading as "something is broken with this sale itself".
const POSTING_STATUS_COLORS: Record<PosPostingStatus, 'success' | 'default' | 'warning' | 'error'> = {
    PENDING: 'warning',
    POSTED: 'success',
    FAILED: 'error',
    SKIPPED: 'default',
    REVERSAL_FAILED: 'warning',
};

// toDayStartIso/toDayEndIso now live in utils/dateUtils.ts (final-review F5 -- this was a
// byte-identical duplicate of PosDayEndReport.tsx's own copy). Used only for the from/to filters
// below -- every other date on this screen (sold/received) is rendered from the server's own ISO
// value via formatDateTime, never round-tripped through either helper.

const emptyFilters = { storeId: '', terminalId: '', cashierUserId: '', fromDate: '', toDate: '' };

const PosSaleList: React.FC = () => {
    const navigate = useNavigate();
    const toast = useToast();
    const { user } = useAuth();
    const userRoles: string[] = user && user.roles
        ? (Array.isArray(user.roles) ? user.roles : [user.roles])
        : [];
    // Terminal and cashier options come from endpoints an Accountant is not admitted to; for that
    // role the two filters simply stay empty (no request, no toast).
    const canLoadStoreOptions = POS_STORE_STAFF_ROLES.some(role => userRoles.includes(role));

    const [stores, setStores] = useState<Store[]>([]);
    const [storeListTruncated, setStoreListTruncated] = useState(false);

    // Terminal and cashier FILTER OPTIONS are scoped to the selected store -- PosService.
    // listTerminals and listStoreAssignments both require a storeId, so these two filter
    // dropdowns are disabled until a store is chosen, exactly like PosTerminalList.tsx /
    // PosCashierAccess.tsx cascade off store.
    const [terminals, setTerminals] = useState<PosTerminal[]>([]);
    const [assignments, setAssignments] = useState<PosStoreAssignment[]>([]);

    const [filters, setFilters] = useState(emptyFilters);

    const [rows, setRows] = useState<PosSaleSummary[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(20);
    const [loading, setLoading] = useState(true);
    // Distinguishes "no sales match these filters" from "the load failed" (final-review F1,
    // same defect as PosCashierAccess.tsx's F1 -- see the comment at PosCashierAccess.tsx:97).
    const [loadError, setLoadError] = useState(false);

    useEffect(() => {
        loadStores();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, size, filters]);

    // Task 7: this used to also drive a per-store loadAllNames() fan-out (1 + 2N requests for N
    // stores -- one terminals call and one store-assignments call per store) purely to resolve
    // the Terminal/Cashier columns' display names. PosSaleSummaryDTO now carries those names
    // directly (resolved server-side, in bulk, per page) -- see the row rendering below -- so
    // this call now only needs to populate the Store FILTER dropdown's own options.
    const loadStores = async () => {
        try {
            const response = await getStoresByInstitutionId(0, STORE_PAGE_SIZE);
            const content = response.content ?? [];
            setStores(content);
            setStoreListTruncated((response.totalElements ?? content.length) > content.length);
        } catch (err) {
            toast.showApiError(err, 'Failed to load stores');
        }
    };

    const load = async () => {
        try {
            setLoading(true);
            setLoadError(false);
            // Every filter here is over sold_at -- the counter's clock -- never received_at. A
            // manager filtering "today's sales" means what the shop sold today, not what the
            // server happened to ingest today; the two differ by however long a terminal was
            // offline, which is the entire point of this product.
            const response = await PosService.listSales({
                storeId: filters.storeId || undefined,
                terminalId: filters.terminalId || undefined,
                cashierUserId: filters.cashierUserId || undefined,
                fromSoldAt: filters.fromDate ? toDayStartIso(filters.fromDate) : undefined,
                toSoldAt: filters.toDate ? toDayEndIso(filters.toDate) : undefined,
                page,
                size,
            });
            setRows(response.content);
            setTotal(response.totalElements);
        } catch (err) {
            setLoadError(true);
            toast.showApiError(err, 'Failed to load sales');
        } finally {
            setLoading(false);
        }
    };

    const handleStoreChange = async (event: SelectChangeEvent) => {
        const storeId = event.target.value;
        setPage(0);
        setFilters(prev => ({ ...prev, storeId, terminalId: '', cashierUserId: '' }));
        setTerminals([]);
        setAssignments([]);
        if (!storeId || !canLoadStoreOptions) return;
        // Independent calls: one failing (e.g. terminals) must not wipe the other's options.
        const [terminalResult, assignmentResult] = await Promise.allSettled([
            PosService.listTerminals(storeId),
            PosService.listStoreAssignments(storeId),
        ]);
        if (terminalResult.status === 'fulfilled') {
            setTerminals(terminalResult.value);
        } else {
            toast.showApiError(terminalResult.reason, 'Failed to load terminal options');
        }
        if (assignmentResult.status === 'fulfilled') {
            setAssignments(assignmentResult.value.filter(a => a.isActive));
        } else {
            toast.showApiError(assignmentResult.reason, 'Failed to load cashier options');
        }
    };

    const handleDateChange = (key: 'fromDate' | 'toDate') => (e: React.ChangeEvent<HTMLInputElement>) => {
        setPage(0);
        setFilters(prev => ({ ...prev, [key]: e.target.value }));
    };

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h5">Sales</Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
                <FormControl sx={{ minWidth: 220 }}>
                    <InputLabel id="pos-sale-store-label">Store</InputLabel>
                    <Select
                        labelId="pos-sale-store-label"
                        label="Store"
                        value={filters.storeId}
                        onChange={handleStoreChange}
                    >
                        <MenuItem value="">All stores</MenuItem>
                        {stores.map(store => (
                            <MenuItem key={store.id} value={store.id}>{store.storeName}</MenuItem>
                        ))}
                    </Select>
                    {storeListTruncated && (
                        <FormHelperText>
                            Showing the first {STORE_PAGE_SIZE} stores.
                        </FormHelperText>
                    )}
                </FormControl>

                <FormControl sx={{ minWidth: 200 }} disabled={!filters.storeId}>
                    <InputLabel id="pos-sale-terminal-label">Terminal</InputLabel>
                    <Select
                        labelId="pos-sale-terminal-label"
                        label="Terminal"
                        value={filters.terminalId}
                        onChange={e => { setPage(0); setFilters(prev => ({ ...prev, terminalId: e.target.value })); }}
                    >
                        <MenuItem value="">All terminals</MenuItem>
                        {terminals.map(terminal => (
                            <MenuItem key={terminal.id} value={terminal.id}>{terminal.terminalCode}</MenuItem>
                        ))}
                    </Select>
                    {!filters.storeId && <FormHelperText>Choose a store first</FormHelperText>}
                </FormControl>

                <FormControl sx={{ minWidth: 200 }} disabled={!filters.storeId}>
                    <InputLabel id="pos-sale-cashier-label">Cashier</InputLabel>
                    <Select
                        labelId="pos-sale-cashier-label"
                        label="Cashier"
                        value={filters.cashierUserId}
                        onChange={e => { setPage(0); setFilters(prev => ({ ...prev, cashierUserId: e.target.value })); }}
                    >
                        <MenuItem value="">All cashiers</MenuItem>
                        {assignments.map(a => (
                            <MenuItem key={a.userId} value={a.userId}>{a.userName}</MenuItem>
                        ))}
                    </Select>
                    {!filters.storeId && <FormHelperText>Choose a store first</FormHelperText>}
                </FormControl>

                <TextField
                    label="From date"
                    type="date"
                    value={filters.fromDate}
                    onChange={handleDateChange('fromDate')}
                    InputLabelProps={{ shrink: true }}
                    // Filters the sold-at date -- the counter's own clock -- never received_at.
                    helperText="Filters by when the sale was sold, not when the server received it"
                />
                <TextField
                    label="To date"
                    type="date"
                    value={filters.toDate}
                    onChange={handleDateChange('toDate')}
                    InputLabelProps={{ shrink: true }}
                />
            </Box>

            {loadError ? (
                <Alert
                    severity="error"
                    action={(
                        <Button color="inherit" size="small" onClick={load}>
                            Retry
                        </Button>
                    )}
                >
                    Failed to load sales. This is NOT the same as no sales matching these filters
                    -- try again before assuming there are none.
                </Alert>
            ) : loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
            ) : (
                <TableContainer component={Paper}>
                    <Box sx={{ px: 2, pt: 1.5 }}>
                        {/* The backing query is native with a baked-in ORDER BY sold_at DESC, id
                            DESC (ruling R3) -- a sort control here would be a control that does
                            nothing, which is worse than no control, so this is static text, not a
                            TableSortLabel. */}
                        <Typography variant="caption" color="text.secondary">
                            Newest first (by sale time)
                        </Typography>
                    </Box>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Receipt</TableCell>
                                <TableCell>Store</TableCell>
                                <TableCell>Terminal</TableCell>
                                <TableCell>Cashier</TableCell>
                                <TableCell>Sold At</TableCell>
                                <TableCell>Total</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Posting</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={8}>
                                        <Typography color="text.secondary">
                                            No sales found for these filters.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                            {rows.map(sale => (
                                <TableRow
                                    key={sale.id}
                                    hover
                                    sx={{ cursor: 'pointer' }}
                                    onClick={() => navigate(`/pos/sales/${sale.id}`)}
                                >
                                    <TableCell>{sale.receiptNo}</TableCell>
                                    <TableCell>{sale.storeName ?? sale.storeId}</TableCell>
                                    <TableCell>{sale.terminalCode ?? sale.terminalId}</TableCell>
                                    <TableCell>{sale.cashierName ?? sale.cashierUserId}</TableCell>
                                    <TableCell>{formatDateTime(sale.soldAt)}</TableCell>
                                    <TableCell>{formatMoney(sale.totalAmount)}</TableCell>
                                    <TableCell>
                                        <Chip size="small" color={SALE_STATUS_COLORS[sale.status]} label={sale.status} />
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            size="small"
                                            color={POSTING_STATUS_COLORS[sale.postingStatus]}
                                            label={sale.postingStatus}
                                        />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    <TablePagination
                        component="div"
                        count={total}
                        page={page}
                        onPageChange={(_, p) => setPage(p)}
                        rowsPerPage={size}
                        rowsPerPageOptions={[10, 20, 50, 100]}
                        onRowsPerPageChange={e => { setSize(Number(e.target.value)); setPage(0); }}
                    />
                </TableContainer>
            )}
        </Box>
    );
};

export default PosSaleList;
